import { useEffect, useState, useCallback, useMemo, useRef, Fragment } from 'react';
import { ChevronDown as ChevronDownIcon, ChevronRight as ChevronRightIcon, Search as SearchIcon, Pencil as PencilIcon, ImageIcon, Upload as UploadIcon, Send as SendIcon } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import {
  fetchMyCampaigns,
  fetchCampaignEvents,
  sendMessage,
  sendImageMessage,
  postVideo,
  updateWorkorderStatus,
  setVisibility,
  type CampaignEvent,
  type UserCampaign,
} from '@/store/slices/myCampaignsSlice';
import { fetchMyProfile } from '@/store/slices/profileSlice';
import { fetchAdvertisingTypes } from '@/store/slices/envsSlice';
import { showSpinner, hideSpinner } from '@/store/slices/helpersSlice';
import type { Admin } from '@/store/slices/envsSlice';
import moment from 'moment';
import { influencerApi } from '@/lib/api';

/**
 * 원본: .old-gc-mono/apps/gc-zoku/components/pages/MyCampaigns/
 * 내 캠페인 대시보드 - 참여 중인 캠페인 목록 + 이벤트 타임라인
 */

// ── 캠페인 그룹 분류 (원본: @gc/common/utils/user) ──

interface CampaignGroup {
  id: string;
  title: string;
  visibility?: { mutable: boolean };
}

const USER_CAMPAIGN_GROUPS: CampaignGroup[] = [
  { id: 'pending', title: 'Pending' },
  { id: 'in_progress', title: 'In progress' },
  { id: 'completed', title: 'Completed' },
  { id: 'incomplete', title: 'Incomplete', visibility: { mutable: true } },
];

function getGroupId(uc: UserCampaign): string {
  switch (uc.status) {
    case 'candidate':
    case 'prospect':
    case 'contacting':
    case 'invited':
    case 'negotiation':
      return 'pending';
    case 'content_creation':
    case 'payment':
    case 'payment_done':
      return 'in_progress';
    case 'completed':
      return 'completed';
    default:
      return 'incomplete';
  }
}

function stepFromStatus(status?: string): number {
  switch (status) {
    case 'candidate':
    case 'prospect':
    case 'contacting':
    case 'invited':
    case 'negotiation':
      return 1;
    case 'content_creation':
      return 2;
    case 'payment':
      return 3;
    case 'payment_done':
    case 'completed':
      return 4;
    case 'rejected':
    case 'incomplete':
      return -1;
    default:
      return 0;
  }
}

function getProgressText(step: number): string {
  switch (step) {
    case 2: return 'Your action needed';
    case 4: return 'Completed';
    case -1: return '';
    default: return 'In progress';
  }
}

function needYourAction(step: number): boolean {
  return step === 2;
}

/**
 * lodash _.words() 호환 — camelCase, 언더스코어, 공백 등으로 단어 분리
 * 예: "MapleStory N_Global" → ["Maple", "Story", "N", "Global"]
 */
function splitWords(str: string): string[] {
  return str.match(/[A-Z]?[a-z]+|[A-Z]+(?=[A-Z][a-z]|\d|\b)|[A-Z]|\d+/g) || [];
}

function makeAbbr(title?: string): string {
  if (!title) return '';
  const w = splitWords(title);
  const first = w[0]?.[0] || '';
  const second = w[1]?.[0] || w[0]?.[w[0].length - 1] || '';
  return (first + second).toUpperCase();
}

function getCampaignData(uc: UserCampaign) {
  return (uc as Record<string, unknown>).campaign as Record<string, unknown> | undefined;
}

function getCampaignName(uc: UserCampaign): string {
  return (getCampaignData(uc)?.name as string) || uc.name || '';
}

function getBanner(uc: UserCampaign): string | undefined {
  const banner = getCampaignData(uc)?.banner as Array<{ url: string }> | undefined;
  return banner?.[0]?.url;
}

// ── MyCampaigns 메인 컴포넌트 ──

export default function MyCampaigns() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { campaignId } = useParams<{ campaignId?: string }>();

  const currentUser = useAppSelector((s) => s.envs.currentUser);
  const profile = useAppSelector((s) => s.profile.myProfile);
  const myCampaigns = useAppSelector((s) => s.myCampaigns.campaigns);
  const eventsMap = useAppSelector((s) => s.myCampaigns.events);
  const admins = useAppSelector((s) => s.envs.admins);
  const advertisingTypes = useAppSelector((s) => s.envs.advertisingTypes);
  const loading = useAppSelector((s) => s.myCampaigns.loading);

  const [filter, setFilter] = useState('');

  useEffect(() => {
    dispatch(showSpinner());
    Promise.all([
      dispatch(fetchMyCampaigns()),
      dispatch(fetchMyProfile()),
      dispatch(fetchAdvertisingTypes()),
    ]).finally(() => dispatch(hideSpinner()));
  }, [dispatch]);

  useEffect(() => {
    if (campaignId) {
      dispatch(showSpinner());
      dispatch(fetchCampaignEvents(campaignId)).finally(() => dispatch(hideSpinner()));
    }
  }, [dispatch, campaignId]);

  const handleSwitchCampaign = useCallback((id: string) => {
    const path = window.location.pathname.startsWith('/bridge')
      ? `/bridge/my_campaigns/${id}`
      : `/my-campaigns/${id}`;
    navigate(path);
  }, [navigate]);

  const handleSetVisibility = useCallback(async (groupId: string, cid: string, isVisible: boolean) => {
    dispatch(showSpinner());
    try {
      await dispatch(setVisibility({ groupId, campaignId: cid, isVisible })).unwrap();
      await dispatch(fetchMyCampaigns());
    } finally {
      dispatch(hideSpinner());
    }
  }, [dispatch]);

  if (loading && !myCampaigns) return null;
  if (!currentUser || !profile) return null;

  if (!myCampaigns || myCampaigns.length === 0) {
    return (
      <div className="mc-empty">
        <div className="mc-empty-alert">
          <span className="mc-empty-icon">ℹ</span>
          <div>
            <p className="mc-empty-title">Nothing to show</p>
            <p className="mc-empty-desc">{"You haven't joined any campaign yet"}</p>
          </div>
        </div>
      </div>
    );
  }

  const selectedCampaign = myCampaigns.find((c) => c.campaignId === campaignId);
  const events = campaignId ? eventsMap[campaignId] : undefined;
  const sortedEvents = events
    ? [...events].sort((a, b) => {
        const dA = (a as Record<string, unknown>).createdAt as string || '';
        const dB = (b as Record<string, unknown>).createdAt as string || '';
        return dB.localeCompare(dA);
      })
    : [];

  return (
    <div id="my-campaigns" className="mc-root">
      {/* ── 좌측 사이드바 ── */}
      <div className="mc-sidebar">
        <div className="mc-search">
          <span className="mc-search-icon">
            <SearchIcon size={14} />
          </span>
          <input
            type="text"
            placeholder="Search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        <ActivitiesMenu
          activities={myCampaigns}
          selectedActivityId={campaignId}
          switchMyCampaign={handleSwitchCampaign}
          filter={filter}
          setVisibility={handleSetVisibility}
        />
      </div>

      {/* ── 우측 메인 영역 ── */}
      <div className="mc-main">
        {selectedCampaign ? (
          <EventsList
            events={sortedEvents}
            campaign={getCampaignData(selectedCampaign)}
            campaignId={selectedCampaign.campaignId}
            userCampaign={selectedCampaign}
            admins={admins}
            profile={profile}
            advertisingTypes={advertisingTypes}
          />
        ) : (
          <div className="mc-placeholder">
            <p>Select a campaign to view details</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── ActivitiesMenu ──

interface ActivitiesMenuProps {
  activities: UserCampaign[];
  selectedActivityId?: string;
  switchMyCampaign: (id: string) => void;
  filter: string;
  setVisibility: (groupId: string, campaignId: string, isVisible: boolean) => void;
}

function ActivitiesMenu({ activities, selectedActivityId, switchMyCampaign, filter, setVisibility }: ActivitiesMenuProps) {
  const grouped = useMemo(() => {
    const map: Record<string, UserCampaign[]> = {};
    activities.forEach((a) => {
      const gid = getGroupId(a);
      if (!map[gid]) map[gid] = [];
      map[gid].push(a);
    });
    return map;
  }, [activities]);

  const selectedActivity = activities.find((a) => a.campaignId === selectedActivityId);
  const defaultGroupId = selectedActivity
    ? getGroupId(selectedActivity)
    : USER_CAMPAIGN_GROUPS.find((g) => grouped[g.id]?.length)?.id;

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    USER_CAMPAIGN_GROUPS.forEach((g) => {
      init[g.id] = g.id === defaultGroupId;
    });
    return init;
  });

  useEffect(() => {
    if (selectedActivity) {
      const gid = getGroupId(selectedActivity);
      setOpenGroups((prev) => ({ ...prev, [gid]: true }));
    }
  }, [selectedActivity]);

  const filterActivities = useCallback((acts: UserCampaign[]) => {
    if (!filter) return acts;
    const re = new RegExp(filter.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
    return acts.filter((a) =>
      a.campaignId === selectedActivityId ||
      re.test(getCampaignName(a)) ||
      re.test((getCampaignData(a)?.gameTitle as string) || '') ||
      re.test(a.status || '')
    );
  }, [filter, selectedActivityId]);

  const toggleGroup = (gid: string) => {
    setOpenGroups((prev) => ({ ...prev, [gid]: !prev[gid] }));
  };

  // nexpace dashboard instruction
  const nexpaceInstruction = selectedActivity
    ? (getCampaignData(selectedActivity)?.nexpace as Record<string, unknown>)?.dashboardInstruction as string
    : undefined;

  return (
    <div className="mc-groups">
      {USER_CAMPAIGN_GROUPS.map((group) => {
        const acts = grouped[group.id];
        if (!acts || acts.length === 0) return null;

        const sumUnread = acts.reduce((s, a) => s + (a.unreadMessageCount || 0), 0);
        const filtered = filterActivities(acts);
        const isOpen = openGroups[group.id];

        return (
          <div key={group.id} className="mc-group">
            <div
              className="mc-group-header"
              onClick={() => toggleGroup(group.id)}
            >
              <span className="mc-group-arrow">{isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}</span>
            <span className="mc-group-title">
                {group.title.toUpperCase()} ({acts.length})
              </span>
              {sumUnread > 0 && <span className="mc-badge">{sumUnread > 99 ? '99+' : sumUnread}</span>}
            </div>
            {isOpen && (
              <div className="mc-group-items">
                {filtered.map((act) => (
                  <ActivityItem
                    key={act.campaignId}
                    activity={act}
                    selected={selectedActivityId === act.campaignId}
                    onClick={() => switchMyCampaign(act.campaignId)}
                    onHide={() => setVisibility(group.id, act.campaignId, false)}
                    group={group}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
      {nexpaceInstruction && (
        <div className="mc-nexpace" dangerouslySetInnerHTML={{ __html: nexpaceInstruction }} />
      )}
    </div>
  );
}

// ── ActivityItem ──

interface ActivityItemProps {
  activity: UserCampaign;
  selected: boolean;
  onClick: () => void;
  onHide: () => void;
  group: CampaignGroup;
}

function ActivityItem({ activity, selected, onClick, onHide, group }: ActivityItemProps) {
  const [confirmHide, setConfirmHide] = useState(false);
  const step = stepFromStatus(activity.status);
  const banner = getBanner(activity);
  const name = getCampaignName(activity);
  const abbr = makeAbbr(name);

  return (
    <div>
      <div
        className={`mc-activity-card ${selected ? 'mc-activity-selected' : ''}`}
        onClick={onClick}
      >
        <div className="mc-activity-avatar-wrap">
          {needYourAction(step) && <span className="mc-action-dot" />}
          {banner ? (
            <img src={banner} alt="" className="mc-activity-avatar" />
          ) : (
            <div className="mc-activity-avatar mc-avatar-text">{abbr}</div>
          )}
        </div>
        <div className="mc-activity-info">
          <div className="mc-activity-name">{name}</div>
          <CampaignProgressBar step={step} />
          <div className="mc-activity-footer">
            <span className="mc-activity-status">{getProgressText(step)}</span>
            <div className="mc-activity-actions">
              {group.visibility?.mutable && (
                <>
                  {!confirmHide ? (
                    <a
                      href="#"
                      className="mc-hide-link"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setConfirmHide(true); }}
                    >Hide</a>
                  ) : (
                    <span className="mc-confirm-hide" onClick={(e) => e.stopPropagation()}>
                      <span>Hide permanently?</span>
                      <button onClick={(e) => { e.stopPropagation(); onHide(); }}>Yes</button>
                      <button onClick={(e) => { e.stopPropagation(); setConfirmHide(false); }}>No</button>
                    </span>
                  )}
                </>
              )}
              {(activity.unreadMessageCount || 0) > 0 && (
                <span className="mc-badge mc-badge-sm">{activity.unreadMessageCount}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 선택 시 상세 정보 표시 */}
      {selected && <ActivityDetails userCampaign={activity} />}
    </div>
  );
}

// ── CampaignProgressBar ──

function CampaignProgressBar({ step }: { step: number }) {
  const steps = [
    { title: 'Negotiation' },
    { title: 'Content creation' },
    { title: 'Payment' },
    { title: 'Complete', isLast: true },
  ];

  return (
    <div className="mc-progress-bar">
      {steps.map((st, i) => {
        const completed = step >= i + 1;
        const active = step === i + 1;
        const isLast = st.isLast;
        let cls = 'mc-progress-step';
        if (isLast && completed) cls += ' mc-step-success';
        else if (completed) cls += ' mc-step-done';
        else if (active) cls += ' mc-step-active';

        return (
          <div key={i} className="mc-progress-step-wrap" title={st.title}>
            <div className={cls}>
              <div className="mc-progress-fill" style={{ width: completed ? '100%' : '0%' }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── ActivityDetails (리퍼럴 링크 + Talking points + Materials) ──

function ActivityDetails({ userCampaign }: { userCampaign: UserCampaign }) {
  const [showTP, setShowTP] = useState(false);
  const links = (userCampaign as Record<string, unknown>).links as Array<{
    id?: string;
    shortUrl: string;
    linkType?: string;
  }> | undefined;

  const campaign = getCampaignData(userCampaign);
  const talkingPoints = campaign?.talkingPoints as string | undefined;
  const materialsFolder = campaign?.materialsFolder as { url: string } | undefined;

  if (!links?.length && !talkingPoints && !materialsFolder) return null;

  return (
    <div className="mc-details-panel">
      {links && links.length > 0 && (
        <>
          <div className="mc-details-divider">Your referral links</div>
          {links.map((l, i) => (
            <div key={l.id || i} className="mc-link-block">
              <button
                className="mc-copy-btn"
                onClick={() => {
                  navigator.clipboard.writeText(l.shortUrl);
                }}
                title="Copy to clipboard"
              >📋</button>
              <a href={l.shortUrl} target="_blank" rel="noopener noreferrer">{l.shortUrl}</a>
            </div>
          ))}
        </>
      )}
      {(talkingPoints || materialsFolder) && (
        <div className="mc-details-buttons">
          {talkingPoints && (
            <button className="mc-btn-dashed" onClick={() => setShowTP(true)}>
              💬 Talking points
            </button>
          )}
          {materialsFolder && (
            <button className="mc-btn-dashed" onClick={() => window.open(materialsFolder.url, '_blank')}>
              📁 Campaign Materials
            </button>
          )}
        </div>
      )}
      {showTP && talkingPoints && (
        <div className="mc-tp-overlay" onClick={() => setShowTP(false)}>
          <div className="mc-tp-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Talking points</h3>
            <div dangerouslySetInnerHTML={{ __html: talkingPoints }} />
            <button className="mc-tp-close" onClick={() => setShowTP(false)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── EventsList (우측 메인 영역) ──

interface EventsListProps {
  events: CampaignEvent[];
  campaign?: Record<string, unknown>;
  campaignId: string;
  userCampaign: UserCampaign;
  admins: Admin[];
  profile: Record<string, unknown>;
  advertisingTypes: unknown[] | null;
}

function EventsList({ events, campaign, campaignId, userCampaign, admins, profile, advertisingTypes }: EventsListProps) {
  const dispatch = useAppDispatch();
  const [text, setText] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [partnership, setPartnership] = useState(
    (userCampaign as Record<string, unknown>).expectedPartnershipType as string || 'pre-roll',
  );

  const prevCampaignId = useRef(campaignId);
  useEffect(() => {
    if (campaignId !== prevCampaignId.current) {
      setText('');
      setVideoUrl('');
      setPartnership((userCampaign as Record<string, unknown>).expectedPartnershipType as string || 'pre-roll');
      prevCampaignId.current = campaignId;
    }
  }, [campaignId, userCampaign]);

  const showPostVideo = userCampaign.status === 'content_creation' || userCampaign.status === 'payment';
  const allowImageMessages = !!(campaign?.nexpace as Record<string, unknown>)?.allowSendingImageMessages;

  const handleSendMessage = useCallback(async () => {
    if (!text.trim()) return;
    dispatch(showSpinner());
    try {
      await dispatch(sendMessage({ campaignId, text: text.trim() })).unwrap();
      setText('');
      await dispatch(fetchCampaignEvents(campaignId));
    } finally {
      dispatch(hideSpinner());
    }
  }, [dispatch, campaignId, text]);

  const handlePostVideo = useCallback(async () => {
    if (!videoUrl.trim()) return;
    dispatch(showSpinner());
    try {
      await dispatch(postVideo({ campaignId, videoUrl: videoUrl.trim(), partnership: partnership === 'true' || undefined })).unwrap();
      setVideoUrl('');
      await dispatch(fetchCampaignEvents(campaignId));
    } finally {
      dispatch(hideSpinner());
    }
  }, [dispatch, campaignId, videoUrl, partnership]);

  const handleSendImage = useCallback(async (imageUrl: string) => {
    dispatch(showSpinner());
    try {
      await dispatch(sendImageMessage({ campaignId, imageUrl: encodeURI(imageUrl) })).unwrap();
      await dispatch(fetchCampaignEvents(campaignId));
    } finally {
      dispatch(hideSpinner());
    }
  }, [dispatch, campaignId]);

  return (
    <div className="mc-events-area">
      <div className="mc-timeline">
        {/* 비디오 제출 */}
        {showPostVideo && (
          <div className="mc-tl-item">
            <div className="mc-tl-dot mc-dot-video">▶</div>
            <div className="mc-tl-content">
              <div className="mc-video-form">
                <div className="mc-video-row">
                  <select
                    value={partnership}
                    onChange={(e) => setPartnership(e.target.value)}
                    className="mc-select"
                  >
                    {(advertisingTypes as Array<{ _id?: string; id?: string; title: string; isObsolete?: boolean }> || [])
                      .filter((t) => !t.isObsolete)
                      .map((t) => (
                        <option key={t._id || t.id} value={t._id || t.id}>{t.title}</option>
                      ))}
                  </select>
                  <input
                    type="text"
                    placeholder="https://www.youtube.com/watch?v=********"
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value.trim())}
                    className="mc-input mc-video-input"
                  />
                </div>
                <button className="mc-btn-primary" onClick={handlePostVideo} disabled={!videoUrl.trim()}>
                  Submit video
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 이미지 메시지 */}
        {allowImageMessages && (
          <ImageMessageItem onSendImage={handleSendImage} />
        )}

        {/* 텍스트 메시지 입력 */}
        <div className="mc-tl-item">
          <div className="mc-tl-dot mc-dot-edit"><PencilIcon size={14} /></div>
          <div className="mc-tl-content">
            <textarea
              placeholder="Type message here..."
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="mc-textarea"
              rows={3}
            />
            <div className="mc-msg-actions">
              <button className="mc-btn-primary min-w-34" onClick={handleSendMessage} disabled={!text.trim()}>
                <SendIcon size={14} /> Send Message
              </button>
            </div>
          </div>
        </div>

        {/* 이벤트 목록 */}
        {events.map((event) => (
          <CampaignEventItem
            key={event.id}
            event={event}
            admins={admins}
            profile={profile}
            advertisingTypes={advertisingTypes}
            campaignId={campaignId}
          />
        ))}

        {events.length === 0 && (
          <p className="mc-no-events">No events yet.</p>
        )}
      </div>
    </div>
  );
}

// ── ImageMessageItem ──

function ImageMessageItem({ onSendImage }: { onSendImage: (url: string) => void }) {
  const [imageUrl, setImageUrl] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    try {
      const data = await influencerApi.postForm<{ url: string }>('upload/image', formData);
      if (data.url) setImageUrl(data.url);
    } catch {
      // upload failed silently
    }
  };

  return (
    <div className="mc-tl-item">
      <div className="mc-tl-dot mc-dot-image">
        <ImageIcon size={16} />
      </div>
      <div className="mc-tl-content">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        {!imageUrl ? (
          <button className="mc-btn-outline min-w-32" onClick={() => fileRef.current?.click()}>
            <UploadIcon size={14} /> Select image
          </button>
        ) : (
          <div className="mc-image-preview">
            <img src={imageUrl} alt="preview" style={{ maxWidth: 200, borderRadius: 4 }} />
          </div>
        )}
        <div className="mc-msg-actions">
          <button
            className="mc-btn-primary min-w-32"
            onClick={() => { onSendImage(imageUrl); setImageUrl(''); }}
            disabled={!imageUrl}
          >
            <SendIcon size={14} /> Send image
          </button>
        </div>
      </div>
    </div>
  );
}

// ── CampaignEventItem ──

interface CampaignEventItemProps {
  event: CampaignEvent;
  admins: Admin[];
  profile: Record<string, unknown>;
  advertisingTypes: unknown[] | null;
  campaignId: string;
}

function CampaignEventItem({ event, admins, profile, advertisingTypes, campaignId }: CampaignEventItemProps) {
  const ed = event as Record<string, unknown>;
  if (ed.status === 'deleted') return null;

  return (
    <div className="mc-tl-item">
      <EventDot event={event} admins={admins} profile={profile} />
      <div className="mc-tl-content">
        <EventHeader event={event} admins={admins} />
        <EventContent event={event} advertisingTypes={advertisingTypes} campaignId={campaignId} />
      </div>
    </div>
  );
}

// ── EventDot ──

function EventDot({ event, admins, profile }: { event: CampaignEvent; admins: Admin[]; profile: Record<string, unknown> }) {
  const ed = event as Record<string, unknown>;
  const sender = ed.sender as string;

  if (sender === 'user') {
    const avatar = profile.avatar as string;
    return avatar
      ? <img src={avatar} alt="" className="mc-tl-dot mc-dot-avatar" />
      : <div className="mc-tl-dot mc-dot-user">👤</div>;
  }

  if (sender === 'admin') {
    const admin = admins.find((a) => a.id === (ed.adminId as string));
    const src = admin?.avatar || '/assets/favicon/favicon-32x32.png';
    return <img src={src} alt="" className="mc-tl-dot mc-dot-avatar mc-dot-admin" />;
  }

  return <img src="/assets/favicon/favicon-32x32.png" alt="" className="mc-tl-dot mc-dot-avatar mc-dot-admin" />;
}

// ── EventHeader ──

function EventHeader({ event, admins }: { event: CampaignEvent; admins: Admin[] }) {
  const ed = event as Record<string, unknown>;
  const sender = ed.sender as string;
  const createdAt = (ed.createdAt || ed.sentAt) as string;

  let title = 'GGContent.com';
  if (sender === 'user') {
    title = 'You';
  } else if (sender === 'admin') {
    const admin = admins.find((a) => a.id === (ed.adminId as string));
    title = admin?.title || 'GGContent.com';
  }

  return (
    <div className="mc-event-header">
      <span className="text-sm font-semibold text-gray-700">{title}</span>
      <span className="text-sm font-normal italic text-gray-400">{createdAt ? moment(createdAt).format('YYYY-MM-DD LT') : ''}</span>
    </div>
  );
}

// ── EventContent ──

function EventContent({ event, advertisingTypes }: { event: CampaignEvent; advertisingTypes: unknown[] | null; campaignId: string }) {
  const ed = event as Record<string, unknown>;
  const kind = ed.kind as string;

  switch (kind) {
    case 'MessageEvent':
      return <MessageEvent event={ed} />;
    case 'WorkOrderEvent':
      return <WorkOrderEvent event={event} advertisingTypes={advertisingTypes} />;
    case 'SubmitVideoEvent':
      return <SubmitVideoEvent event={ed} />;
    default:
      return ed.content ? <div className="mc-event-text">{ed.content as string}</div> : null;
  }
}

// ── MessageEvent ──

function MessageEvent({ event }: { event: Record<string, unknown> }) {
  const html = (event.content || event.text) as string || '';
  return <div className="mc-event-text" dangerouslySetInnerHTML={{ __html: html }} />;
}

// ── SubmitVideoEvent ──

function SubmitVideoEvent({ event }: { event: Record<string, unknown> }) {
  const videoUrl = event.videoUrl as string;
  const videoTitle = event.videoTitle as string || videoUrl;
  const status = event.status as string;
  const rejectionFeedback = event.rejectionFeedback as string;

  const statusText = (() => {
    switch (status) {
      case 'new': return 'Waiting for approval';
      case 'approved': return 'Video has been approved';
      case 'rejected': return 'Video has been rejected';
      case 'deleted': return 'Video has been removed';
      default: return '';
    }
  })();

  return (
    <div className="mc-video-event">
      <div className="mc-video-meta">
        <span className="mc-video-icon">▶</span>
        <div>
          <a href={videoUrl} target="_blank" rel="noopener noreferrer" className="mc-video-title">{videoTitle}</a>
          <span className={`mc-video-status mc-vs-${status}`}>{statusText}</span>
        </div>
      </div>
      {status === 'rejected' && rejectionFeedback && (
        <div className="mc-rejection">
          <span>Advertiser Feedback:</span>
          <div className="mc-rejection-text">
            {rejectionFeedback.split('\n').map((line, i) => (
              <Fragment key={i}>{i > 0 && <br />}{line}</Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── WorkOrderEvent ──

function WorkOrderEvent({ event, advertisingTypes }: { event: CampaignEvent; advertisingTypes: unknown[] | null }) {
  const dispatch = useAppDispatch();
  const ed = event as Record<string, unknown>;
  const items = ed.items as Array<Record<string, unknown>> || [];
  const status = ed.status as string;
  const gameTitle = (ed.gameTitle || '-') as string;
  const channelTitle = (ed.channelTitle || '-') as string;
  const uid = ed.uid as string | number;
  const price = ed.price as number || 0;

  const advTypesMap = useMemo(() => {
    if (!advertisingTypes) return {} as Record<string, string>;
    return (advertisingTypes as Array<{ _id?: string; id?: string; title: string }>).reduce((acc, t) => {
      const key = t._id || t.id || '';
      acc[key] = t.title;
      return acc;
    }, {} as Record<string, string>);
  }, [advertisingTypes]);

  const handleAccept = async () => {
    dispatch(showSpinner());
    try {
      await dispatch(updateWorkorderStatus({ eventId: event.id, status: 'accepted' })).unwrap();
    } finally {
      dispatch(hideSpinner());
    }
  };

  const handleDecline = async () => {
    dispatch(showSpinner());
    try {
      await dispatch(updateWorkorderStatus({ eventId: event.id, status: 'declined' })).unwrap();
    } finally {
      dispatch(hideSpinner());
    }
  };

  return (
    <div className="mc-workorder">
      <div className="mc-wo-title">Work order #{uid}</div>
      <table className="mc-wo-table">
        <thead>
          <tr>
            <th>Game</th>
            <th>Channel</th>
            <th>Type</th>
            <th>Count</th>
            <th>Price</th>
          </tr>
        </thead>
        <tbody>
          {items.length > 0 ? items.map((item, i) => (
            <tr key={i}>
              {i === 0 && <td rowSpan={items.length}>{gameTitle}</td>}
              {i === 0 && <td rowSpan={items.length}>{channelTitle}</td>}
              <td>{advTypesMap[(item.advertisingType as string)] || (item.advertisingType as string)}</td>
              <td>{item.count as number}</td>
              <td>{item.price != null ? `$${Number(item.price).toLocaleString()}` : '-'}</td>
            </tr>
          )) : (
            <tr>
              <td>{gameTitle}</td>
              <td>{channelTitle}</td>
              <td colSpan={3}>-</td>
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={5}><strong>Total</strong>: ${price.toLocaleString()}</td>
          </tr>
        </tfoot>
      </table>

      {status === 'new' && (
        <div className="mc-wo-actions">
          <button className="mc-btn-primary" onClick={handleAccept}>Accept</button>
          <button className="mc-btn-danger" onClick={handleDecline}>Decline</button>
        </div>
      )}
      {status === 'accepted' && <div className="mc-wo-alert mc-wo-success">Work order accepted</div>}
      {status === 'declined' && <div className="mc-wo-alert mc-wo-warning">Work order declined</div>}
      {status === 'canceled' && <div className="mc-wo-alert mc-wo-warning">Work order canceled</div>}
    </div>
  );
}
